import React, { useState } from 'react';
import styled from '@emotion/styled';
import { txaPushPaymentStatus } from '@atlassian/cssxp-types-billing';
import { creditPolicyResponse } from '@atlassian/cssxp-types-quote';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { ValueType } from '@atlaskit/select';
import { ProgressTracker, Stages } from '@atlaskit/progress-tracker';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import Button, { LoadingButton } from '@atlaskit/button';
import { ContactInfo, InvoiceGroup, PaymentMethodType } from '@css-xp/graphtypes/src/generated-types-new';
import type { BillingProfileOption } from './order-summary';
import { BillingProfileActionsEnum } from './types';
import { ChangePONumber } from './change-po-number';
import { ChangeBillingProfile } from './change-billing-profile';
import { TermPaymentValidationError } from './term-payment-validation-error';

interface Props {
  isOpen: boolean;
  onClose: (hasAddedPrimaryContactInTheModal?: boolean) => void;
  billingProfileAction: BillingProfileActionsEnum | undefined;
  preSelectedBillingProfile: InvoiceGroup | null | undefined;
  billingProfilesInfo: InvoiceGroup[] | null | undefined;
  isUpdatingPONumberFailed: boolean;
  setIsUpdatingPONumberFailed: (status: boolean) => void;
  updatingPONumber: boolean;
  onSavePONumber: (selectedPONumber: string, selectedBillingProfile: ValueType<BillingProfileOption>) => void;
  onSave: (
    selectedBillingProfile: ValueType<BillingProfileOption>,
    selectedPONumber: string,
    atlassianAccountIds?: string[],
    atlassianAccountEmails?: string[],
    hasAddedPrimaryContactInTheModal?: boolean
  ) => void;
  checkFinancePolicies?: () => Promise<creditPolicyResponse>;
  isCheckingFinancePolicies?: boolean;
  isDeferredPaymentMethodDisabled: boolean;
  isNoPaymentMethodDisabled: boolean;
  showPricingEligibility?: boolean;
  shouldAllowBillingProfileTransferBasedOnPricingEligibility?: boolean;
  allowedPricingEligibilityForBillingProfileTransfer?: string[];
  transactionAccountId?: string;
  showPrimaryContacts?: boolean;
  primaryContacts?: ContactInfo[];
  isPayPalEnabled?: boolean;
  secondaryCustomerContacts?: ContactInfo[];
  atlassianAccountIds?: string[];
  setAtlassianAccountIds?: React.Dispatch<React.SetStateAction<string[]>>;
  atlassianAccountEmails?: string[];
  setAtlassianAccountEmails?: React.Dispatch<React.SetStateAction<string[]>>;
  hasAddedPrimaryContactInTheModal?: boolean;
  existingPrimaryContact?: ContactInfo[];
}

const StyledBillingProfilePONumberContainer = styled.div({
  gap: '16px',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '3px',
  boxShadow: '0px 8px 12px 0px rgba(9, 30, 66, 0.15)',
  padding: '24px 24px 16px 24px',
});

const StyledBillingProfilePONumberHeader = styled.label({
  fontSize: '20px',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#172b4d',
  textAlign: 'left',
  fontVariationSettings: 'wdth 100',
});

const StyledBillingProfilePONumberActions = styled.div({
  display: 'flex',
  justifyContent: 'end',
  gap: '8px',
});

const StyledProgressTracker = styled.div({
  marginBottom: '10px',
});

export const BillingProfileAndPONumberModal = ({
  isOpen,
  onClose,
  billingProfilesInfo,
  preSelectedBillingProfile,
  isUpdatingPONumberFailed,
  setIsUpdatingPONumberFailed,
  updatingPONumber,
  onSavePONumber,
  onSave,
  billingProfileAction,
  checkFinancePolicies,
  isCheckingFinancePolicies = false,
  isDeferredPaymentMethodDisabled = false,
  isNoPaymentMethodDisabled = false,
  showPricingEligibility = false,
  shouldAllowBillingProfileTransferBasedOnPricingEligibility = false,
  allowedPricingEligibilityForBillingProfileTransfer = [],
  transactionAccountId = '',
  showPrimaryContacts,
  primaryContacts,
  isPayPalEnabled = false,
  secondaryCustomerContacts,
  atlassianAccountIds,
  setAtlassianAccountIds,
  atlassianAccountEmails,
  setAtlassianAccountEmails,
}: Props) => {
  const [selectedBillingProfile, setSelectedBillingProfile] = useState<ValueType<BillingProfileOption> | null>({
    label: preSelectedBillingProfile?.name || '',
    value: preSelectedBillingProfile?.id || '',
  });

  const [hasExistingPrimaryContact, setHasExistingPrimaryContact] = useState(false);
  const [selectedPONumber, setSelectedPONumber] = useState('');
  const [shouldDisableSaveOrNextButton, setShouldDisableSaveOrNextButton] = useState(false);

  React.useEffect(() => {
    if (showPrimaryContacts) {
      const hasExistPrimaryContact = primaryContacts?.find(
        (contact) => contact?.identifierId === selectedBillingProfile?.value
      );
      setHasExistingPrimaryContact(hasExistPrimaryContact ? true : false);
      const shouldDisableSaveOrNextButton = !hasExistPrimaryContact && atlassianAccountIds?.[0] === '';
      setShouldDisableSaveOrNextButton(shouldDisableSaveOrNextButton);
    }
  }, [selectedBillingProfile?.value, atlassianAccountIds, primaryContacts]);

  const [financePolicyResponse, setFinancePolicyResponse] = useState<creditPolicyResponse>();

  const items: Stages = [
    {
      id: 'CHANGE_BILLING_PROFILE',
      label: 'Billing profile',
      percentageComplete: 0,
      status: 'current',
    },
    {
      id: 'CHANGE_PO_NUMBER',
      label: 'PO number',
      percentageComplete: 0,
      status: 'unvisited',
    },
  ];

  const [progressTrackerStage, setProgressTrackerStage] = useState<BillingProfileActionsEnum>(
    BillingProfileActionsEnum.CHANGE_BILLING_PROFILE
  );
  const [progressTrackerItems, setProgressTrackerItems] = useState<Stages>(items);

  const onNext = () => {
    setIsUpdatingPONumberFailed(false);
    // FIXME :: create method for below items array as used in multiple places within this file.
    setProgressTrackerItems([
      {
        id: 'CHANGE_BILLING_PROFILE',
        label: 'Billing profile',
        percentageComplete: 100,
        status: 'visited',
        onClick: () => {
          setProgressTrackerItems(items);
          setProgressTrackerStage(BillingProfileActionsEnum.CHANGE_BILLING_PROFILE);
        },
      },
      {
        id: 'CHANGE_PO_NUMBER',
        label: 'PO number',
        percentageComplete: 0,
        status: 'current',
      },
    ]);
    setProgressTrackerStage(BillingProfileActionsEnum.CHANGE_PO_NUMBER);
  };

  const onCancel = () => {
    setProgressTrackerItems(items);
    setProgressTrackerStage(BillingProfileActionsEnum.CHANGE_BILLING_PROFILE);
    onClose();
  };

  const sectionErrorMessage = (
    <SectionMessage title="We’re unable to update the PO number" appearance="error">
      <p>Try entering the PO number again. If this keeps happening, contact Partner Service Desk.</p>
    </SectionMessage>
  );

  const handleBIllingProfileSave = async () => {
    let isPaymentEligible = true;
    if (checkFinancePolicies) {
      const selectPaymentType = billingProfilesInfo?.find(
        (billingProfile) => billingProfile.id === selectedBillingProfile?.value
      )?.defaultPaymentMethod?.type;
      if (selectPaymentType === PaymentMethodType.DEFERRED) {
        try {
          const response = await checkFinancePolicies();
          isPaymentEligible = response?.status === txaPushPaymentStatus.ELIGIBLE;
          setFinancePolicyResponse(response);
        } catch (err) {
          setFinancePolicyResponse({ status: txaPushPaymentStatus.INELIGIBLE });
          isPaymentEligible = false;
        }
      }
    }
    if (isPaymentEligible) {
      if (showPrimaryContacts) {
        const hasExistingPrimaryInSelectedBp = primaryContacts?.find(
          (contact: ContactInfo) => contact?.identifierId === selectedBillingProfile?.value
        );
        onSave(
          selectedBillingProfile,
          selectedPONumber,
          atlassianAccountIds,
          atlassianAccountEmails,
          hasExistingPrimaryInSelectedBp ? false : true
        );
      } else onSave(selectedBillingProfile, selectedPONumber);

      if (checkFinancePolicies) {
        setFinancePolicyResponse({} as creditPolicyResponse);
      }
    }
  };

  const handleSave = () => {
    if (showPrimaryContacts) {
      const hasExistingPrimaryInSelectedBp = primaryContacts?.find(
        (contact: ContactInfo) => contact?.identifierId === selectedBillingProfile?.value
      );
      onSave(
        selectedBillingProfile,
        selectedPONumber,
        atlassianAccountIds,
        atlassianAccountEmails,
        hasExistingPrimaryInSelectedBp ? false : true
      );
    } else onSave(selectedBillingProfile, selectedPONumber);
  };

  const defaultPONumber = billingProfilesInfo?.find(
    (billingProfileInfo) => selectedBillingProfile?.value === billingProfileInfo.id
  )?.purchaseOrder?.number;

  const disablePaymentMethodReason = React.useMemo(
    () =>
      isPayPalEnabled && isDeferredPaymentMethodDisabled && isNoPaymentMethodDisabled ? (
        <SectionMessage title={''} appearance="information">
          For monthly subscription orders, only billing profiles with a credit card or a PayPal payment method can be
          selected.
        </SectionMessage>
      ) : isDeferredPaymentMethodDisabled && isNoPaymentMethodDisabled ? (
        <SectionMessage title={''} appearance="information">
          For monthly subscription orders, only billing profiles with a credit card payment method can be selected.
        </SectionMessage>
      ) : isNoPaymentMethodDisabled ? (
        <SectionMessage title={''} appearance="information">
          For subscription orders, only billing profiles with a payment method can be selected.
        </SectionMessage>
      ) : null,
    [isDeferredPaymentMethodDisabled, isNoPaymentMethodDisabled, isPayPalEnabled]
  );

  return (
    <ModalTransition>
      {isOpen && (
        <Modal
          onClose={() => onClose()}
          testId="add-billing-profile"
          shouldCloseOnOverlayClick={false}
          shouldScrollInViewport
        >
          {BillingProfileActionsEnum.NEW_BILLING_PROFILE === billingProfileAction && (
            <StyledBillingProfilePONumberContainer>
              <StyledBillingProfilePONumberHeader>Select billing profile</StyledBillingProfilePONumberHeader>
              <StyledProgressTracker>
                <ProgressTracker
                  testId="progress-tracker-billing-profile"
                  items={progressTrackerItems}
                  spacing="compact"
                />
              </StyledProgressTracker>
              {progressTrackerStage === BillingProfileActionsEnum.CHANGE_BILLING_PROFILE && (
                <>
                  {disablePaymentMethodReason}
                  <ChangeBillingProfile
                    billingProfilesInfo={billingProfilesInfo}
                    selectedBillingProfile={selectedBillingProfile}
                    setSelectedBillingProfile={setSelectedBillingProfile}
                    isDeferredPaymentMethodDisabled={isDeferredPaymentMethodDisabled}
                    isNoPaymentMethodDisabled={isNoPaymentMethodDisabled}
                    showPricingEligibility={showPricingEligibility}
                    shouldAllowBillingProfileTransferBasedOnPricingEligibility={
                      shouldAllowBillingProfileTransferBasedOnPricingEligibility
                    }
                    allowedPricingEligibilityForBillingProfileTransfer={
                      allowedPricingEligibilityForBillingProfileTransfer
                    }
                    showPrimaryContacts={showPrimaryContacts}
                    primaryContacts={primaryContacts}
                    isPaypalEnabled={isPayPalEnabled}
                    atlassianAccountIds={atlassianAccountIds}
                    atlassianAccountEmails={atlassianAccountEmails}
                    setAtlassianAccountEmails={setAtlassianAccountEmails}
                    setAtlassianAccountIds={setAtlassianAccountIds}
                    secondaryCustomerContacts={secondaryCustomerContacts}
                  />
                  <StyledBillingProfilePONumberActions>
                    <Button onClick={() => onClose()}>Cancel</Button>
                    <Button
                      appearance="primary"
                      onClick={() => onNext()}
                      isDisabled={selectedBillingProfile?.value === '' || shouldDisableSaveOrNextButton}
                    >
                      Next
                    </Button>
                  </StyledBillingProfilePONumberActions>
                </>
              )}

              {progressTrackerStage === BillingProfileActionsEnum.CHANGE_PO_NUMBER && (
                <>
                  {isUpdatingPONumberFailed && sectionErrorMessage}
                  <ChangePONumber setSelectedPONumber={setSelectedPONumber} defaultPONumber={defaultPONumber} />
                  <StyledBillingProfilePONumberActions>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button
                      isDisabled={updatingPONumber}
                      iconAfter={updatingPONumber ? <Spinner size="small" /> : undefined}
                      appearance="primary"
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                  </StyledBillingProfilePONumberActions>
                </>
              )}
            </StyledBillingProfilePONumberContainer>
          )}
          {BillingProfileActionsEnum.CHANGE_BILLING_PROFILE === billingProfileAction && (
            <StyledBillingProfilePONumberContainer>
              <StyledBillingProfilePONumberHeader>Switch billing profile</StyledBillingProfilePONumberHeader>
              {financePolicyResponse?.status === txaPushPaymentStatus.INELIGIBLE && (
                <div className="push-payment-validation-error">
                  <TermPaymentValidationError
                    financePolicyResponse={financePolicyResponse}
                    transactionAccountId={transactionAccountId}
                  />
                </div>
              )}
              {disablePaymentMethodReason}
              <ChangeBillingProfile
                billingProfilesInfo={billingProfilesInfo}
                selectedBillingProfile={selectedBillingProfile}
                setSelectedBillingProfile={setSelectedBillingProfile}
                isDeferredPaymentMethodDisabled={isDeferredPaymentMethodDisabled}
                isNoPaymentMethodDisabled={isNoPaymentMethodDisabled}
                showPricingEligibility={showPricingEligibility}
                shouldAllowBillingProfileTransferBasedOnPricingEligibility={
                  shouldAllowBillingProfileTransferBasedOnPricingEligibility
                }
                allowedPricingEligibilityForBillingProfileTransfer={allowedPricingEligibilityForBillingProfileTransfer}
                showPrimaryContacts={showPrimaryContacts}
                primaryContacts={primaryContacts}
                isPaypalEnabled={isPayPalEnabled}
                atlassianAccountIds={atlassianAccountIds}
                atlassianAccountEmails={atlassianAccountEmails}
                setAtlassianAccountEmails={setAtlassianAccountEmails}
                setAtlassianAccountIds={setAtlassianAccountIds}
                secondaryCustomerContacts={secondaryCustomerContacts}
              />
              <StyledBillingProfilePONumberActions>
                <Button
                  onClick={() => {
                    if (financePolicyResponse?.status) {
                      setFinancePolicyResponse({} as creditPolicyResponse);
                    }
                    setSelectedBillingProfile({
                      label: preSelectedBillingProfile?.name || '',
                      value: preSelectedBillingProfile?.id || '',
                    });
                    if (showPrimaryContacts) {
                      const hasExistingContactInPreSelectedBp = primaryContacts?.find(
                        (contact: ContactInfo) => contact?.identifierId === preSelectedBillingProfile?.id
                      );
                      onClose(hasExistingContactInPreSelectedBp ? false : true);
                    } else onClose();
                  }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  appearance="primary"
                  onClick={handleBIllingProfileSave}
                  isDisabled={isCheckingFinancePolicies || shouldDisableSaveOrNextButton}
                  iconAfter={isCheckingFinancePolicies ? <Spinner size="small" /> : undefined}
                >
                  Save
                </LoadingButton>
              </StyledBillingProfilePONumberActions>
            </StyledBillingProfilePONumberContainer>
          )}
          {BillingProfileActionsEnum.CHANGE_PO_NUMBER === billingProfileAction && (
            <StyledBillingProfilePONumberContainer>
              <StyledBillingProfilePONumberHeader>PO number</StyledBillingProfilePONumberHeader>
              {isUpdatingPONumberFailed && sectionErrorMessage}
              <ChangePONumber setSelectedPONumber={setSelectedPONumber} defaultPONumber={defaultPONumber} />
              <StyledBillingProfilePONumberActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                  isDisabled={updatingPONumber}
                  iconAfter={updatingPONumber ? <Spinner size="small" /> : undefined}
                  appearance="primary"
                  onClick={() => onSavePONumber(selectedPONumber, selectedBillingProfile)}
                >
                  Save
                </Button>
              </StyledBillingProfilePONumberActions>
            </StyledBillingProfilePONumberContainer>
          )}
        </Modal>
      )}
    </ModalTransition>
  );
};
